import App from 'app';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import reportWebVitals from 'reportWebVitals';

// when a stale-asset reload was started, so the errors thrown while the page unloads aren't reported.
// Time-bounded rather than a flag: the reload can be cancelled from UnsavedChangesPrompt's beforeunload
// dialog, and the tab must then go back to reporting errors and recovering from stale assets.
const STALE_ASSET_RELOAD_WINDOW_MS = 10000;
let staleAssetReloadStartedAt = 0;
const isReloadingForStaleAssets = () => Date.now() - staleAssetReloadStartedAt < STALE_ASSET_RELOAD_WINDOW_MS;

// sentry
Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    // semver release (e.g. openfield-web@5.65.0+1234) on production deploys, build number elsewhere
    release: process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_BUILD_NUMBER,
    ignoreErrors: [
        'ResizeObserver loop completed with undelivered notifications', // benign: https://sentry.io/answers/react-resizeobserver-loop-completed-with-undelivered-notifications/
    ],
    beforeSend: (event) => (isReloadingForStaleAssets() ? null : event),
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
        // records evaluated LaunchDarkly flags on each error; see the inspector in FeatureFlagProvider
        Sentry.launchDarklyIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.01, // set the sample rate to 1% for performance monitoring
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', process.env.REACT_APP_API_HOST || ''],
    // Session Replay
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 1%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Vite fires this when a lazy route chunk or its CSS 404s — typically a tab left open
// across a deploy, where the old hashed assets no longer exist. Reload to pick up the
// new asset manifest; the timestamp guard avoids a reload loop if assets stay unreachable.
// One lazy import can fire this more than once (its CSS, then its JS chunk), so later events
// on the same page load must also be suppressed rather than tripping the guard and throwing.
window.addEventListener('vite:preloadError', (event) => {
    if (isReloadingForStaleAssets()) {
        event.preventDefault();
        return;
    }

    const RELOADED_AT_KEY = 'preload-error-reloaded-at';
    const lastReload = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);

    if (Date.now() - lastReload < 30000) {
        return;
    }

    staleAssetReloadStartedAt = Date.now();
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    event.preventDefault();
    window.location.reload();
});

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);
root.render(<App />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
